import * as React from 'react'
import {
  type ColumnDef,
  getCoreRowModel,
  useReactTable,
  flexRender,
} from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { components } from '@/lib/api/generated'

type POItem = components['schemas']['POLineItemResponse']

// Receive-state stripe (3px left edge on each row):
//   - 'todo'    (orange, --receive-todo) = nothing received yet. Kept at the
//     original orange on purpose: it's the to-do list staff work down at the
//     bench, so the cue they know must not move. The token is FROZEN.
//   - 'partial' (amber, --meter-attention) = some received, not all. Darker
//     than the orange so the two stay distinguishable.
//   - 'done'    (green, --meter-good) = fully received (including over).
// Exported so the page's legend counts lines with the same rule.
export type ReceiveState = 'todo' | 'partial' | 'done'

export function receiveStateOf(item: POItem): ReceiveState {
  if (item.qty_received >= item.qty_ordered) return 'done'
  if (item.qty_received === 0) return 'todo'
  return 'partial'
}

const STATE_BG: Record<ReceiveState, string> = {
  todo: 'bg-[var(--receive-todo)]',
  partial: 'bg-[var(--meter-attention)]',
  done: 'bg-[var(--meter-good)]',
}

interface ManualReceiveButtonsProps {
  item: POItem
  onManualReceive: (item: POItem, qty: number) => void
}

// −/+ pair. "+" bumps qty_received by 1. "−" subtracts 1 (used to correct
// over-counts from miscount or returned items). "−" is disabled when nothing
// has been received yet so the user can't push qty_received negative.
function ManualReceiveButtons({ item, onManualReceive }: ManualReceiveButtonsProps) {
  const canDecrement = item.qty_received > 0
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-[var(--ink-2)] hover:text-[var(--text-primary)] disabled:opacity-30"
        aria-label={`Subtract one ${item.product_title}`}
        data-testid={`manual-unreceive-${item.po_item_id}`}
        disabled={!canDecrement}
        onClick={() => onManualReceive(item, -1)}
      >
        −
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-[var(--ink-2)] hover:text-[var(--text-primary)]"
        aria-label={`Receive one ${item.product_title}`}
        data-testid={`manual-receive-${item.po_item_id}`}
        onClick={() => onManualReceive(item, 1)}
      >
        +
      </Button>
    </div>
  )
}

// Barcode cell. When an item already has a barcode we just render it. When it
// lacks one (stored as empty string), we show an inline input + Save so the
// user can assign one without leaving the receiving screen. A hardware scan
// into the focused input fills it and Enter commits (the window-level scanner
// ignores keystrokes while an input is focused).
function BarcodeCell({
  item,
  onAssignBarcode,
  pending,
}: {
  item: POItem
  onAssignBarcode: (item: POItem, barcode: string) => void
  pending: boolean
}) {
  const [value, setValue] = React.useState('')

  if (item.barcode) {
    return (
      <span className="font-mono text-xs text-[var(--ink-2)]">{item.barcode}</span>
    )
  }

  const submit = () => {
    const trimmed = value.trim()
    if (!trimmed) return
    onAssignBarcode(item, trimmed)
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            submit()
          }
        }}
        placeholder="Add barcode"
        disabled={pending}
        className="w-full min-w-0 border border-[var(--line-input)] bg-transparent px-1 py-0.5 font-mono text-[11px] focus:border-[var(--brand)]"
        data-testid={`assign-barcode-input-${item.po_item_id}`}
        aria-label={`Assign barcode for ${item.product_title}`}
      />
      <Button
        variant="ghost"
        size="sm"
        className="h-6 shrink-0 px-1.5 text-[10px]"
        disabled={pending || !value.trim()}
        onClick={submit}
        data-testid={`assign-barcode-${item.po_item_id}`}
      >
        {pending ? '…' : 'Save'}
      </Button>
    </div>
  )
}

interface ReceivingTableProps {
  items: POItem[]
  onManualReceive: (item: POItem, qty: number) => void
  onAssignBarcode: (item: POItem, barcode: string) => void
  assignBarcodePendingId?: number | null
}

export function ReceivingTable({
  items,
  onManualReceive,
  onAssignBarcode,
  assignBarcodePendingId,
}: ReceivingTableProps) {
  const columns = React.useMemo<ColumnDef<POItem>[]>(
    () => [
      {
        id: 'sig',
        header: () => null,
        cell: ({ row }) => (
          <div
            className={cn('h-[28px] w-[3px]', STATE_BG[receiveStateOf(row.original)])}
            data-testid={`receive-stripe-${row.original.po_item_id}`}
          />
        ),
        size: 4,
      },
      {
        id: 'item',
        header: 'Item',
        cell: ({ row }) => {
          const { product_title, variant_title, qty_ordered, qty_received, notes } =
            row.original
          const remaining = qty_ordered - qty_received
          const partial = qty_received > 0 && qty_received < qty_ordered
          return (
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5 truncate">
                <span className="truncate text-xs font-medium text-[var(--text-primary)]">
                  {product_title}
                </span>
                {variant_title && (
                  <span className="truncate text-xs text-[var(--mute)]">
                    · {variant_title}
                  </span>
                )}
                {partial && (
                  <span
                    className="inline-block shrink-0 rounded-full bg-[var(--status-awaiting-bg)] px-2 py-0.5 text-[10.5px] font-semibold leading-none whitespace-nowrap text-[var(--status-awaiting-fg)]"
                    data-testid={`receive-left-${row.original.po_item_id}`}
                  >
                    {remaining} left
                  </span>
                )}
              </div>
              {/* Per-line item note (buyer context, e.g. a customer special
                  order) — the warehouse needs it while receiving. */}
              {(notes ?? '').trim() && (
                <div className="truncate text-[11px] italic text-[var(--mute)]">
                  {notes}
                </div>
              )}
            </div>
          )
        },
      },
      {
        id: 'sku',
        header: 'SKU',
        size: 130,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-[var(--ink-2)]">
            {row.original.sku || '—'}
          </span>
        ),
      },
      {
        id: 'barcode',
        header: 'Barcode',
        size: 170,
        cell: ({ row }) => (
          <BarcodeCell
            item={row.original}
            onAssignBarcode={onAssignBarcode}
            pending={assignBarcodePendingId === row.original.po_item_id}
          />
        ),
      },
      {
        id: 'ord',
        header: 'Ordered',
        size: 80,
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="text-xs tabular-nums text-[var(--ink-2)]">
            {row.original.qty_ordered}
          </span>
        ),
      },
      {
        id: 'rcv',
        header: 'Received',
        size: 84,
        meta: { numeric: true },
        cell: ({ row }) => (
          <span className="text-xs font-semibold tabular-nums text-[var(--text-primary)]">
            {row.original.qty_received}
          </span>
        ),
      },
      {
        id: 'remaining',
        header: 'Remaining',
        size: 92,
        meta: { numeric: true },
        // Units still owed = ordered − received, floored at 0 so an
        // over-receipt reads "0 remaining" rather than a negative number.
        cell: ({ row }) => {
          const remaining = Math.max(
            0,
            row.original.qty_ordered - row.original.qty_received
          )
          return (
            <span
              className={cn(
                'text-xs tabular-nums',
                remaining > 0 ? 'text-[var(--ink-2)]' : 'text-[var(--mute)]'
              )}
            >
              {remaining}
            </span>
          )
        },
      },
      {
        id: 'progress',
        header: 'Progress',
        size: 130,
        cell: ({ row }) => {
          const { qty_ordered, qty_received } = row.original
          const pct =
            qty_ordered > 0
              ? Math.min(100, Math.round((qty_received / qty_ordered) * 100))
              : 0
          return (
            <div
              className="relative h-[6px] w-full overflow-hidden border border-[var(--line-warm)] bg-[var(--meter-track)]"
              aria-label={`${pct}% received`}
            >
              <div
                className={cn(
                  'absolute inset-y-0 left-0',
                  qty_ordered > 0 && qty_received >= qty_ordered
                    ? 'bg-[var(--meter-good)]'
                    : 'bg-[var(--brand)]'
                )}
                style={{ width: pct + '%' }}
              />
            </div>
          )
        },
      },
      {
        id: 'last_scan',
        header: 'Last scan',
        size: 90,
        cell: () => <span className="text-[11px] text-[var(--mute)]">—</span>,
      },
      {
        id: 'actions',
        header: 'Manual receive',
        // Widened slightly to fit the new header label without wrapping.
        size: 110,
        meta: { numeric: true },
        cell: ({ row }) => (
          <ManualReceiveButtons
            item={row.original}
            onManualReceive={onManualReceive}
          />
        ),
      },
    ],
    [onManualReceive, onAssignBarcode, assignBarcodePendingId]
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="flex-1 overflow-auto">
      <table className="w-full table-fixed border-collapse">
        <colgroup>
          {columns.map((c) => (
            <col key={c.id} style={c.size ? { width: c.size } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {table.getHeaderGroups()[0].headers.map((header) => {
              const numeric =
                (header.column.columnDef.meta as { numeric?: boolean } | undefined)
                  ?.numeric ?? false
              return (
                <th
                  key={header.id}
                  className={cn(
                    'tbl-th border-b border-[var(--line-warm)] text-left',
                    numeric && 'text-right'
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, idx) => (
            <tr
              key={row.id}
              data-testid={`receive-row-${row.original.po_item_id}`}
              className={
                idx % 2 === 0 ? 'bg-[var(--surface-card)]' : 'bg-[var(--surface-zebra)]'
              }
            >
              {row.getVisibleCells().map((cell) => {
                const numeric =
                  (cell.column.columnDef.meta as { numeric?: boolean } | undefined)
                    ?.numeric ?? false
                const isSig = cell.column.id === 'sig'
                return (
                  <td
                    key={cell.id}
                    className={cn(
                      'border-b border-[var(--line-warm)] align-middle',
                      isSig ? 'p-0' : 'px-2 py-2 text-xs',
                      numeric && 'text-right tabular-nums'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
